import { Controller, useFormContext } from "react-hook-form";
import { useState } from "react";
import CustomTextInput from "../components/CustomTextInput";
import CustomSelect from "../components/CustomSelect";
import PaginatedSelect from "./PaginatedSelect";
import { Button } from "flowbite-react";
import {
  useGetPrioritiesQuery,
} from "../api/ticketApi/selectInputApis";
import { useGetPaginatedCategoriesQuery } from "@/api/ticketApi/category";
import { useGetClientsQuery } from "@/api/clientApi/client";
import { useGetAgentsQuery } from "@/api/agentApi/agent";
import { useGetClientGroupsQuery } from "../api/clientGroupApi/clientGroup";
import { Ticket } from "@/sharedTypes";
import { useTranslation } from "react-i18next";

interface TicketFormFilterProps {
  filter: Ticket;
  onFilterApply: (filters: Ticket) => void;
  onCancel: () => void;
  showInactive: boolean;
  setShowInactive: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function TicketFormFilter({
  filter,
  onFilterApply,
  showInactive,
  setShowInactive,
}: TicketFormFilterProps) {
  const { t } = useTranslation("ticket");
  const { control, handleSubmit, reset, register } = useFormContext<any>();

  const [categoryPage, setCategoryPage] = useState(1);
  const [categorySearch, setCategorySearch] = useState("");
  
  const [clientPage, setClientPage] = useState(1);
  const [clientSearch, setClientSearch] = useState("");

  const [agentPage, setAgentPage] = useState(1);
  const [agentSearch, setAgentSearch] = useState("");

  const [clientGroupPage, setClientGroupPage] = useState(1);
  const [clientGroupSearch, setClientGroupSearch] = useState("");

  const refetchOpts = { refetchOnMountOrArgChange: true as const };
  const { data: priorities } = useGetPrioritiesQuery(undefined, refetchOpts);
  
  const { data: categoriesData, isFetching: isCategoriesFetching } = useGetPaginatedCategoriesQuery(
    {
      pagination: { page: categoryPage, pageSize: 5 },
      filters: { "category_name[like]": categorySearch }
    },
    refetchOpts
  );

  const { data: clientsData, isFetching: isClientsFetching } = useGetClientsQuery(
    {
      page: clientPage,
      pageSize: 5,
      client_username: clientSearch
    },
    refetchOpts
  );

  const { data: agentsData, isFetching: isAgentsFetching } = useGetAgentsQuery(
    {
      page: agentPage,
      pageSize: 5,
      agent_username: agentSearch
    },
    refetchOpts
  );

  const { data: clientGroupsData, isFetching: isClientGroupsFetching } = useGetClientGroupsQuery(
    {
      pagination: { page: clientGroupPage, pageSize: 5 },
      filters: { group_name: clientGroupSearch }
    },
    refetchOpts
  );

  // Derived Options with Safe Defaults
  const categories = categoriesData?.items || [];
  const clients = clientsData?.items || [];
  const agents = agentsData?.items || [];
  const clientGroups = clientGroupsData?.items || [];

  // Options for Paginated Selects
  const categoryOptions = categories.map((c: any) => ({
      label: c.category_name,
      value: String(c.category_id)
  }));

  const clientOptions = clients.map((c: any) => ({
      label: c.client_username,
      value: String(c.client_id)
  }));

  const agentOptions = agents.map((a: any) => ({
      label: a.agent_username,
      value: String(a.agent_id)
  }));

  const clientGroupOptions = clientGroups.map((g: any) => ({
      label: g.group_name,
      value: String(g.client_group_id)
  }));

  const onSubmit = (data: Ticket) => {
    const filters = { ...data, is_active: showInactive ? undefined : false };
    onFilterApply(filters);
  };

  const handleReset = () => {
    reset();
    setCategorySearch("");
    setCategoryPage(1);
    setClientSearch("");
    setClientPage(1);
    setAgentSearch("");
    setAgentPage(1);
    setClientGroupSearch("");
    setClientGroupPage(1);
    onFilterApply({
      ...filter,
      is_active: showInactive ? undefined : false,
    });
  };

  const channels = [
    { value: "0", label: t("ticketFilter.options.platform") },
    { value: "1", label: t("ticketFilter.options.mail") },
    { value: "2", label: t("ticketFilter.options.widget") },
  ];

  const statuses = [
    { value: "1", label: t("ticketFilter.options.open") },
    { value: "2", label: t("ticketFilter.options.pending") },
    { value: "3", label: t("ticketFilter.options.resolved") },
  ];

  return (
    <div className="max-h-[75vh] overflow-y-auto p-5">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Inactive Toggle */}
        <div className="flex items-center">
          <button
            type="button"
            onClick={() => {
              reset();
              setShowInactive((prev) => !prev);
            }}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-all ${
              showInactive
                ? "bg-purple-100 text-purple-700 ring-1 ring-purple-300 hover:bg-purple-200 dark:bg-purple-900/30 dark:text-purple-300 dark:ring-purple-700 dark:hover:bg-purple-900/50"
                : "bg-gray-100 text-gray-600 ring-1 ring-gray-200 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:ring-gray-600 dark:hover:bg-gray-600"
            }`}
          >
            <div className={`h-2.5 w-2.5 rounded-full ${showInactive ? "bg-purple-500" : "bg-gray-400"}`} />
            {showInactive ? t("ticketFilter.hideInactive") : t("ticketFilter.showInactive")}
          </button>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {/* Title */}
          <div className="sm:col-span-2">
            <CustomTextInput
              label={t("ticketFilter.title")}
              placeholder={t("ticketFilter.titlePlaceholder")}
              {...register("ticket_title")}
            />
          </div>

          {/* Start Date */}
          <div>
            <CustomTextInput
              id="startDate"
              label={t("ticketFilter.startDate")}
              type="date"
              {...register("startDate")}
            />
          </div>

          {/* End Date */}
          <div>
            <CustomTextInput
              id="endDate"
              label={t("ticketFilter.endDate")}
              type="date"
              {...register("endDate")}
            />
          </div>

          {/* Category */}
          <div>
            <Controller
              name="last_category_id"
              control={control}
              render={({ field }) => (
                <PaginatedSelect
                  id="last_category_id"
                  label={t("ticketFilter.category")}
                  options={categoryOptions}
                  value={field.value ? { label: categoryOptions.find((o: any) => o.value === String(field.value))?.label || String(field.value), value: String(field.value) } : null}
                  onChange={(val: any) => field.onChange(val ? Number(val.value) : undefined)}
                  onInputChange={(val) => { setCategorySearch(val); setCategoryPage(1); }}
                  isLoading={isCategoriesFetching}
                  placeholder={t("ticketFilter.categoryPlaceholder", "Select Category")}
                  page={categoryPage}
                  totalPages={categoriesData?.pagination?.totalPages || 1}
                  onPageChange={setCategoryPage}
                  isClearable
                />
              )}
            />
          </div>

          {/* Priority */}
          <div>
            <Controller
              name="last_priority_id"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  label={t("ticketFilter.priority")}
                  options={priorities?.items?.map((priority: any) => ({
                    value: priority.priority_id?.toString(),
                    label: priority.priority_name,
                  })) || []}
                  value={field.value?.toString() || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    field.onChange(v ? parseInt(v) : undefined);
                  }}
                />
              )}
            />
          </div>

          {/* Channel */}
          <div>
            <Controller
              name="channel"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  label={t("ticketFilter.channel")}
                  options={channels}
                  value={field.value?.toString() || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    field.onChange(v ? parseInt(v) : undefined);
                  }}
                />
              )}
            />
          </div>

          {/* Status */}
          <div>
            <Controller
              name="last_status_id"
              control={control}
              render={({ field }) => (
                <CustomSelect
                  label={t("ticketFilter.status")}
                  options={statuses}
                  value={field.value?.toString() || ""}
                  onChange={(e) => {
                    const v = e.target.value;
                    field.onChange(v ? parseInt(v) : undefined);
                  }}
                />
              )}
            />
          </div>

          {/* Client */}
          <div>
            <Controller
              name="client_id"
              control={control}
              render={({ field }) => (
                <PaginatedSelect
                  id="client_id"
                  label={t("ticketFilter.client")}
                  options={clientOptions}
                  value={field.value ? { label: clientOptions.find((o: any) => o.value === String(field.value))?.label || String(field.value), value: String(field.value) } : null}
                  onChange={(val: any) => field.onChange(val ? Number(val.value) : undefined)}
                  onInputChange={(val) => { setClientSearch(val); setClientPage(1); }}
                  isLoading={isClientsFetching}
                  placeholder={t("ticketFilter.clientPlaceholder")}
                  page={clientPage}
                  totalPages={clientsData?.pagination?.totalPages || 1}
                  onPageChange={setClientPage}
                  isClearable
                />
              )}
            />
          </div>

          {/* Client Group */}
          <div>
            <Controller
              name="client_group_id"
              control={control}
              render={({ field }) => (
                <PaginatedSelect
                  id="client_group_id"
                  label={t("ticketFilter.clientGroup")}
                  options={clientGroupOptions}
                  value={field.value ? { label: clientGroupOptions.find((o: any) => o.value === String(field.value))?.label || String(field.value), value: String(field.value) } : null}
                  onChange={(val: any) => field.onChange(val ? Number(val.value) : undefined)}
                  onInputChange={(val) => { setClientGroupSearch(val); setClientGroupPage(1); }}
                  isLoading={isClientGroupsFetching}
                  placeholder={t("ticketFilter.clientGroup", "Select User Group")}
                  page={clientGroupPage}
                  totalPages={clientGroupsData?.pagination?.totalPages || 1}
                  onPageChange={setClientGroupPage}
                  isClearable
                />
              )}
            />
          </div>

          {/* Assigned Agent */}
          <div>
            <Controller
              name="last_assigned_agent_id"
              control={control}
              render={({ field }) => (
                <PaginatedSelect
                  id="last_assigned_agent_id"
                  label={t("ticketFilter.assignedAgent")}
                  options={agentOptions}
                  value={field.value ? { label: agentOptions.find((o: any) => o.value === String(field.value))?.label || String(field.value), value: String(field.value) } : null}
                  onChange={(val: any) => field.onChange(val ? Number(val.value) : undefined)}
                  onInputChange={(val) => { setAgentSearch(val); setAgentPage(1); }}
                  isLoading={isAgentsFetching}
                  placeholder={t("ticketFilter.assignedAgent", "Select Agent")}
                  page={agentPage}
                  totalPages={agentsData?.pagination?.totalPages || 1}
                  onPageChange={setAgentPage}
                  isClearable
                />
              )}
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
          <Button
            type="button"
            color="gray"
            onClick={handleReset}
            className="px-5"
          >
            {t("ticketFilter.reset")}
          </Button>
          <Button
            type="submit"
            color="blue"
            className="px-5"
          >
            {t("ticketFilter.apply")}
          </Button>
        </div>
      </form>
    </div>
  );
}
